import { FC, memo, ReactNode, useCallback, useMemo } from 'react'

import { Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { Folder, Image, InsertDriveFile, MusicNote, Videocam } from '@mui/icons-material'

import { useTranslation } from 'react-i18next'

import { getImageUrl } from 'src/utils/getImageUrl'

import { LOCALES } from 'src/utils'

import { Folder as MentorCloudFolder, mentorCloudExtensions, MentorCloudFile } from 'src/types/files/file'

interface MentorCloudFileProps {
  file?: MentorCloudFile
  folder?: MentorCloudFolder
  isActive?: boolean
  onClickFile?: (file: MentorCloudFile) => void
  onClickFolder?: (folder: MentorCloudFolder) => void
  view: 'grid' | 'list'
  renderAfter?: (file?: MentorCloudFile | MentorCloudFolder) => ReactNode
}

export const MentorCloudFileItem: FC<MentorCloudFileProps> = memo(props => {
  const { file, folder, view, isActive, onClickFile, onClickFolder, renderAfter } = props

  const { t } = useTranslation('translation', { keyPrefix: 'PAGE.MENTORCLOUD' })
  const theme = useTheme()

  const renderIcon = useCallback(
    (large?: boolean) => {
      const isLarge = large && view !== 'list'

      if (folder) {
        return <Folder fontSize={isLarge ? 'large' : 'inherit'} color={'primary'} />
      }

      if (mentorCloudExtensions.image.find(extension => extension === file?.extension)) {
        // eslint-disable-next-line jsx-a11y/alt-text
        return <Image fontSize={isLarge ? 'large' : 'inherit'} color={'primary'} />
      }

      if (mentorCloudExtensions.video.find(extension => extension === file?.extension)) {
        return <Videocam fontSize={isLarge ? 'large' : 'inherit'} color={'primary'} />
      }

      if (mentorCloudExtensions.audio.find(extension => extension === file?.extension)) {
        return <MusicNote fontSize={isLarge ? 'large' : 'inherit'} color={'primary'} />
      }

      return <InsertDriveFile fontSize={isLarge ? 'large' : 'inherit'} color={'primary'} />
    },
    [file?.extension, folder, view]
  )

  const Preview = useMemo(() => {
    if (mentorCloudExtensions.image.find(imageExtension => imageExtension === file?.extension)) {
      return (
        <img
          src={getImageUrl(file?.file_id || '')}
          alt={file?.name}
          width={view === 'grid' ? '100%' : theme.spacing(8)}
          height={view === 'grid' ? theme.spacing(27) : theme.spacing(8)}
          style={{
            userSelect: 'none',
            objectFit: 'contain',
            background: theme.palette.secondary[300],
            borderRadius: theme.spacing(1.5)
          }}
        />
      )
    }

    if (file?.preview_link || file?.preview_file_id) {
      return (
        <img
          src={file?.preview_file_id ? getImageUrl(file.preview_file_id) : file?.preview_link || ''}
          alt={file?.name}
          width={view === 'grid' ? '100%' : theme.spacing(8)}
          height={view === 'grid' ? theme.spacing(27) : theme.spacing(8)}
          style={{
            userSelect: 'none',
            objectFit: 'contain',
            background: theme.palette.secondary[300],
            borderRadius: theme.spacing(1.5)
          }}
        />
      )
    }

    return (
      <Stack
        width={view === 'grid' ? '100%' : theme.spacing(8)}
        height={view === 'grid' ? theme.spacing(27) : theme.spacing(8)}
        alignItems={'center'}
        justifyContent={'center'}
        style={{
          userSelect: 'none',
          objectFit: 'contain',
          background: theme.palette.secondary[300],
          borderRadius: theme.spacing(1.5)
        }}
      >
        {renderIcon(true)}
      </Stack>
    )
  }, [file?.preview_link, file?.preview_file_id, file?.extension, file?.file_id, file?.name, view, theme, renderIcon])

  if (folder) {
    return (
      <Stack
        border={isActive ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.background.default}`}
        sx={{
          '&:hover': {
            border: `1px solid ${theme.palette.primary.main}`
          },
          transition: 'all .1s ease-in-out',
          cursor: 'default'
        }}
        onDoubleClick={() => onClickFolder?.(folder)}
        borderRadius={theme.spacing(1.5)}
        padding={theme.spacing(1)}
        justifyContent={'start'}
        width={'100%'}
      >
        <Stack
          gap={theme.spacing(2)}
          direction={view === 'grid' ? 'column' : 'row'}
          justifyContent={'start'}
          alignItems={'center'}
          overflow={'hidden'}
          width={'100%'}
        >
          <Stack
            width={view === 'grid' ? '100%' : theme.spacing(8)}
            height={view === 'grid' ? theme.spacing(27) : theme.spacing(8)}
            alignItems={'center'}
            justifyContent={'center'}
            style={{
              objectFit: 'contain',
              background: theme.palette.secondary[300],
              borderRadius: theme.spacing(1.5)
            }}
          >
            {renderIcon(true)}
          </Stack>
          <Stack
            fontSize={theme.spacing(4)}
            alignItems={'center'}
            direction={'row'}
            gap={theme.spacing(1)}
            width={'100%'}
          >
            <Stack
              gap={theme.spacing(1)}
              width={'100%'}
              justifyContent={view === 'grid' ? 'start' : 'space-between'}
              alignItems={'center'}
              direction={'row'}
              overflow={'hidden'}
            >
              {view === 'grid' && renderIcon()}
              <Tooltip title={folder?.name}>
                <Typography
                  textOverflow={'ellipsis'}
                  style={{ cursor: 'default', overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none' }}
                  variant='caption'
                >
                  {folder?.name}
                </Typography>
              </Tooltip>
              <Stack alignItems={'center'} direction={view === 'grid' ? 'column' : 'row'} gap={theme.spacing(4)}>
                {view === 'list' && (
                  <>
                    {renderAfter?.(folder)}
                    <Typography variant='caption'>
                      {folder?.created_at_epoch &&
                        new Date(folder?.created_at_epoch).toLocaleString(LOCALES, {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>
            {view === 'grid' && renderAfter?.(folder)}
          </Stack>
        </Stack>
      </Stack>
    )
  }

  if (view === 'list') {
    return (
      <Stack
        bgcolor={theme.palette.background.paper}
        border={isActive ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.background.default}`}
        sx={{
          '&:hover': {
            border: `1px solid ${theme.palette.primary.main}`
          },
          transition: 'all .1s ease-in-out',
          cursor: 'pointer'
        }}
        onClick={() => file && onClickFile?.(file)}
        borderRadius={theme.spacing(1.5)}
        padding={theme.spacing(1)}
        justifyContent={'start'}
        direction={'row'}
        width={'100%'}
      >
        <Stack
          gap={theme.spacing(2)}
          direction={'row'}
          justifyContent={'start'}
          alignItems={'center'}
          overflow={'hidden'}
          width={'100%'}
        >
          {Preview}
          <Stack
            fontSize={theme.spacing(4)}
            alignItems={'center'}
            direction={'row'}
            gap={theme.spacing(1)}
            width={'100%'}
          >
            <Stack
              gap={theme.spacing(4)}
              width={'100%'}
              justifyContent={'space-between'}
              alignItems={'center'}
              direction={'row'}
              overflow={'hidden'}
            >
              <Tooltip title={file?.name}>
                <Typography
                  textOverflow={'ellipsis'}
                  style={{ cursor: 'default', overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none' }}
                  variant='caption'
                >
                  {file?.name}
                </Typography>
              </Tooltip>
              <Stack alignItems={'center'} direction={'row'} gap={theme.spacing(4)}>
                <Typography variant='caption' color={theme.palette.primary[500]}>
                  {file?.size && (file?.size / (1024 * 1024)).toFixed(2)} {t('size')}
                </Typography>
                <Typography>{renderIcon()}</Typography>
                {renderAfter?.(file)}
                <Typography variant='caption'>
                  {file?.created_at_epoch &&
                    new Date(file?.created_at_epoch).toLocaleString(LOCALES, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack
      bgcolor={theme.palette.background.paper}
      border={isActive ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.background.default}`}
      sx={{
        '&:hover': {
          border: `1px solid ${theme.palette.primary.main}`
        },
        transition: 'all .1s ease-in-out',
        cursor: 'pointer'
      }}
      onClick={() => file && onClickFile?.(file)}
      borderRadius={theme.spacing(1.5)}
      padding={theme.spacing(1)}
      justifyContent={'center'}
      direction={'row'}
      width={'100%'}
    >
      <Stack gap={theme.spacing(2)} justifyContent={'center'} overflow={'hidden'} width={'100%'}>
        {Preview}
        <Stack fontSize={theme.spacing(4)} width={'100%'} direction={'row'} gap={theme.spacing(1)}>
          {renderIcon()}
          <Stack width={'100%'} overflow={'hidden'}>
            <Tooltip title={file?.name}>
              <Typography
                textOverflow={'ellipsis'}
                style={{ cursor: 'default', overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none' }}
                variant='caption'
              >
                {file?.name}
              </Typography>
            </Tooltip>
            <Typography variant='caption' color={theme.palette.primary[500]}>
              {file?.size && (file.size / (1024 * 1024)).toFixed(2)} {t('size')}
            </Typography>
          </Stack>
          {renderAfter?.(file)}
        </Stack>
      </Stack>
    </Stack>
  )
})
